import { useState, useMemo, useEffect, useCallback } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '@/lib/supabase';

interface TeamRecord {
  id: string;
  registration_id: string;
  team_name: string;
  college_name: string;
  department: string;
  city: string;
  team_email: string;
  team_phone: string;
  status: string;
  has_faculty_mentor: boolean;
  leader_id: string;
  created_at: string;
  participants: { id: string; full_name: string; contact_number: string; email: string; year_of_study: string; department: string; is_leader: boolean }[];
  faculty: { id: string; name: string; designation: string; department: string; contact_number: string; email: string } | null;
  payment: { id: string; razorpay_order_id: string | null; razorpay_payment_id: string | null; amount: number; status: string } | null;
}

export default function TeamRegistrations() {
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [search, setSearch] = useState('');
  const [collegeFilter, setCollegeFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedTeam, setSelectedTeam] = useState<TeamRecord | null>(null);
  const perPage = 8;

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const { data: teamsData, error: teamsErr } = await supabase
        .from('teams')
        .select('id, registration_id, team_name, college_name, department, city, team_email, team_phone, status, has_faculty_mentor, leader_id, created_at')
        .order('created_at', { ascending: false });

      if (teamsErr) throw teamsErr;
      if (!teamsData || teamsData.length === 0) {
        setTeams([]);
        setLoading(false);
        return;
      }

      const teamIds = teamsData.map((t) => t.id);

      const [{ data: participants }, { data: mentors }, { data: payments }] = await Promise.all([
        supabase.from('participants').select('id, team_id, full_name, contact_number, email, year_of_study, department, course_type, is_leader').in('team_id', teamIds),
        supabase.from('faculty_mentors').select('id, team_id, name, designation, department, contact_number, email').in('team_id', teamIds),
        supabase.from('payments').select('id, team_id, razorpay_order_id, razorpay_payment_id, amount, status').in('team_id', teamIds),
      ]);

      const partMap: Record<string, TeamRecord['participants']> = {};
      (participants || []).forEach((p) => {
        if (!partMap[p.team_id]) partMap[p.team_id] = [];
        partMap[p.team_id].push(p);
      });

      const mentMap: Record<string, TeamRecord['faculty']> = {};
      (mentors || []).forEach((m) => {
        mentMap[m.team_id] = { id: m.id, name: m.name, designation: m.designation, department: m.department, contact_number: m.contact_number, email: m.email };
      });

      const payMap: Record<string, TeamRecord['payment']> = {};
      (payments || []).forEach((p) => {
        payMap[p.team_id] = { id: p.id, razorpay_order_id: p.razorpay_order_id, razorpay_payment_id: p.razorpay_payment_id, amount: Number(p.amount), status: p.status };
      });

      const merged: TeamRecord[] = teamsData.map((t) => ({
        ...t,
        participants: partMap[t.id] || [],
        faculty: mentMap[t.id] || null,
        payment: payMap[t.id] || null,
      }));

      setTeams(merged);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[Teams] Fetch error:', msg);
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const colleges = useMemo(() => [...new Set(teams.map((t) => t.college_name))].sort(), [teams]);
  const departments = useMemo(() => [...new Set(teams.map((t) => t.department))].sort(), [teams]);
  const cities = useMemo(() => [...new Set(teams.map((t) => t.city))].sort(), [teams]);

  const filtered = useMemo(() => {
    let result = teams;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((t) =>
        t.team_name.toLowerCase().includes(q) ||
        t.registration_id.toLowerCase().includes(q) ||
        t.college_name.toLowerCase().includes(q) ||
        t.participants.some((p) => p.full_name.toLowerCase().includes(q))
      );
    }
    if (collegeFilter) result = result.filter((t) => t.college_name === collegeFilter);
    if (deptFilter) result = result.filter((t) => t.department === deptFilter);
    if (cityFilter) result = result.filter((t) => t.city === cityFilter);
    if (statusFilter) result = result.filter((t) => t.status === statusFilter);
    if (paymentFilter) result = result.filter((t) => (t.payment?.status || 'pending') === paymentFilter);
    return result;
  }, [teams, search, collegeFilter, deptFilter, cityFilter, statusFilter, paymentFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      if (sortField === 'team_name') { aVal = a.team_name; bVal = b.team_name; }
      else if (sortField === 'college_name') { aVal = a.college_name; bVal = b.college_name; }
      else if (sortField === 'city') { aVal = a.city; bVal = b.city; }
      else { aVal = a.created_at; bVal = b.created_at; }
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filtered, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const clearFilters = () => {
    setSearch('');
    setCollegeFilter('');
    setDeptFilter('');
    setCityFilter('');
    setStatusFilter('');
    setPaymentFilter('');
  };

  const hasFilters = search || collegeFilter || deptFilter || cityFilter || statusFilter || paymentFilter;

  const paymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-accent-100 text-accent-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'failed': return 'bg-red-100 text-red-700';
      default: return 'bg-background-200 text-foreground-500';
    }
  };

  const regStatusColor = (status: string) =>
    status === 'verified' ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-500';

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Team Registrations" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Teams' }]}>
        {fetchError && (
          <div className="mb-5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load teams: {fetchError}. <button onClick={fetchTeams} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        {/* Filters */}
        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 mb-5">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between mb-4">
            <div className="relative w-full lg:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="Search name, ID, college..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={collegeFilter} onChange={(e) => { setCollegeFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Colleges</option>
                {colleges.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Departments</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={cityFilter} onChange={(e) => { setCityFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Cities</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Status</option>
                <option value="verified">Verified</option>
                <option value="registered">Registered</option>
              </select>
              <select value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Payments</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
              {hasFilters && (
                <button onClick={clearFilters} className="px-3 py-2 rounded-lg text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-close-line mr-1"></i>Clear
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-foreground-400">{loading ? 'Loading...' : `${sorted.length} team${sorted.length !== 1 ? 's' : ''} found`}</p>
        </div>

        {/* Table */}
        <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      {[
                        { label: 'Reg ID', field: 'registration_id' }, { label: 'Team Name', field: 'team_name' },
                        { label: 'College', field: 'college_name' }, { label: 'Department', field: 'department' },
                        { label: 'City', field: 'city' }, { label: 'Team Leader', field: '' },
                        { label: 'Contact', field: '' }, { label: 'Members', field: '' },
                        { label: 'Payment', field: '' }, { label: 'Status', field: '' },
                        { label: 'Date', field: 'created_at' }, { label: 'Actions', field: '' },
                      ].map((col) => (
                        <th key={col.label} className={`px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap ${col.field ? 'cursor-pointer hover:text-foreground-700' : ''}`}
                          onClick={() => col.field && handleSort(col.field)}>
                          <span className="flex items-center gap-1">{col.label}{col.field && sortField === col.field && <i className={sortDir === 'asc' ? 'ri-arrow-up-s-line text-xs' : 'ri-arrow-down-s-line text-xs'}></i>}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((team) => (
                      <tr key={team.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-foreground-500 whitespace-nowrap">{team.registration_id}</td>
                        <td className="px-4 py-3 font-medium text-foreground-900 whitespace-nowrap">{team.team_name}</td>
                        <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{team.college_name}</td>
                        <td className="px-4 py-3 text-foreground-600 whitespace-nowrap text-xs">{team.department}</td>
                        <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{team.city}</td>
                        <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{team.participants.find((p) => p.is_leader)?.full_name || '—'}</td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{team.team_phone}</td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-background-100 text-xs font-medium text-foreground-600">{team.participants.length}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${paymentStatusColor(team.payment?.status || 'pending')}`}>
                            {(team.payment?.status || 'pending').charAt(0).toUpperCase() + (team.payment?.status || 'pending').slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${regStatusColor(team.status)}`}>
                            {team.status.charAt(0).toUpperCase() + team.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">
                          {new Date(team.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <button onClick={() => setSelectedTeam(team)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-primary-50 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="View Details">
                              <i className="ri-eye-line text-sm"></i>
                            </button>
                            <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Edit">
                              <i className="ri-edit-line text-sm"></i>
                            </button>
                            {team.status !== 'verified' && (
                              <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Verify">
                                <i className="ri-check-line text-sm"></i>
                              </button>
                            )}
                            <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {paginated.length === 0 && (
                      <tr><td colSpan={12} className="px-4 py-12 text-center text-foreground-400">
                        <i className="ri-inbox-line text-3xl block mb-2"></i>No teams found{teams.length > 0 ? ' matching your filters' : '. Registrations will appear here.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t border-background-200/70">
                  <p className="text-xs text-foreground-400">Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, sorted.length)} of {sorted.length}</p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
                      <i className="ri-arrow-left-s-line text-sm"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button key={p} onClick={() => setCurrentPage(p)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${p === currentPage ? 'bg-primary-500 text-white' : 'text-foreground-600 hover:bg-background-100'}`}>
                        {p}
                      </button>
                    ))}
                    <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Modal */}
        {selectedTeam && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedTeam(null)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-background-50 border-b border-background-200/70 p-5 flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{selectedTeam.team_name}</h3>
                  <p className="text-xs text-foreground-400 font-mono">{selectedTeam.registration_id}</p>
                </div>
                <button onClick={() => setSelectedTeam(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><p className="text-xs text-foreground-400">College</p><p className="text-sm font-medium text-foreground-900">{selectedTeam.college_name}</p></div>
                  <div><p className="text-xs text-foreground-400">Department</p><p className="text-sm font-medium text-foreground-900">{selectedTeam.department}</p></div>
                  <div><p className="text-xs text-foreground-400">City</p><p className="text-sm font-medium text-foreground-900">{selectedTeam.city}</p></div>
                  <div><p className="text-xs text-foreground-400">Contact</p><p className="text-sm font-medium text-foreground-900">{selectedTeam.team_phone}</p></div>
                  <div><p className="text-xs text-foreground-400">Email</p><p className="text-sm font-medium text-foreground-900">{selectedTeam.team_email}</p></div>
                  <div><p className="text-xs text-foreground-400">Payment</p>
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold mt-1 ${paymentStatusColor(selectedTeam.payment?.status || 'pending')}`}>
                      {(selectedTeam.payment?.status || 'pending').charAt(0).toUpperCase() + (selectedTeam.payment?.status || 'pending').slice(1)}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="font-heading text-sm font-bold text-foreground-950 mb-3">Participants ({selectedTeam.participants.length})</h4>
                  <div className="space-y-2">
                    {selectedTeam.participants.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-background-100/70">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary-600">{p.full_name[0]}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">
                              {p.full_name}{p.is_leader && <span className="ml-2 text-[10px] text-accent-600 bg-accent-50 px-1.5 py-0.5 rounded-full">Leader</span>}
                            </p>
                            <p className="text-xs text-foreground-400">{p.email} &bull; {p.contact_number} &bull; {p.course_type || ''} &bull; {p.year_of_study} &bull; {p.department}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {selectedTeam.faculty && (
                  <div>
                    <h4 className="font-heading text-sm font-bold text-foreground-950 mb-2">Faculty Mentor</h4>
                    <div className="p-3 rounded-lg bg-background-100/70">
                      <p className="text-sm font-medium text-foreground-900">{selectedTeam.faculty.name}</p>
                      <p className="text-xs text-foreground-400">{selectedTeam.faculty.designation}, {selectedTeam.faculty.department} &bull; {selectedTeam.faculty.email}</p>
                    </div>
                  </div>
                )}
                {selectedTeam.payment && selectedTeam.payment.razorpay_order_id && (
                  <div>
                    <h4 className="font-heading text-sm font-bold text-foreground-950 mb-2">Payment Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="p-3 rounded-lg bg-background-100/70"><p className="text-foreground-400">Order ID</p><p className="font-mono text-foreground-700">{selectedTeam.payment.razorpay_order_id}</p></div>
                      <div className="p-3 rounded-lg bg-background-100/70"><p className="text-foreground-400">Payment ID</p><p className="font-mono text-foreground-700">{selectedTeam.payment.razorpay_payment_id || '—'}</p></div>
                      <div className="p-3 rounded-lg bg-background-100/70"><p className="text-foreground-400">Amount</p><p className="font-bold text-foreground-900">₹{selectedTeam.payment.amount.toLocaleString()}</p></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}